// Records the last three pages visited in the authed shell and pops them for the breadcrumb back button.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  type NavStackEntry,
  jumpNavStack,
  popNavStack,
  pushNavStack,
  readNavStack,
} from '@/lib/navStack';

export function useNavStack(currentLabel: string): {
  stack: NavStackEntry[];
  goBack: () => void;
  jumpTo: (index: number) => void;
} {
  const location = useLocation();
  const navigate = useNavigate();
  const currentTo = `${location.pathname}${location.search}`;
  const skipRecord = useRef(false);
  const lastPage = useRef<NavStackEntry | null>(null);
  const [stack, setStack] = useState<NavStackEntry[]>(() => readNavStack());

  useEffect(() => {
    const previous = lastPage.current;
    lastPage.current = { to: currentTo, label: currentLabel || 'Page' };
    if (!previous || previous.to === currentTo) return;
    if (skipRecord.current) {
      skipRecord.current = false;
      return;
    }
    setStack(pushNavStack(previous));
  }, [currentTo, currentLabel]);

  const goBack = () => {
    const target = popNavStack();
    setStack(readNavStack());
    if (!target) return;
    skipRecord.current = true;
    navigate(target.to);
  };

  const jumpTo = (index: number) => {
    const target = jumpNavStack(index);
    setStack(readNavStack());
    if (!target) return;
    skipRecord.current = true;
    navigate(target.to);
  };

  return { stack, goBack, jumpTo };
}
